import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import api from '../../api/client';
import { skipOnboarding, completeStep } from '../../api/onboarding';
import { analytics } from '../../services/analytics';
import type { OnboardingStatus } from '../../api/onboarding';

interface Props {
  initialStatus: OnboardingStatus;
  onClose: () => void;
  onComplete: () => void;
}

const VOICES = [
  { id: 'EXAVITQu4vr4xnSDxMaL', label: 'Rachel — Warm & Conversational' },
  { id: 'ErXwobaYiN019PkySvjV', label: 'Antoni — Professional & Clear' },
  { id: 'MF3mGyEYCl7XYWbV9V6O', label: 'Elli — Friendly & Energetic' },
];

type CallStatus = 'idle' | 'calling' | 'connected' | 'complete' | 'error';

export const OnboardingWizard: React.FC<Props> = ({ initialStatus, onClose, onComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(Math.min(initialStatus.currentStep, 5));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 2: Agent creation
  const [agentName, setAgentName] = useState('Sales Assistant');
  const [voiceId, setVoiceId] = useState((VOICES[0] as typeof VOICES[number]).id);
  const [personality, setPersonality] = useState<'friendly' | 'professional' | 'casual'>('friendly');
  const [greeting, setGreeting] = useState(
    "Hi there! I'm your AI assistant. How can I help you today?"
  );
  const [createdAgentId, setCreatedAgentId] = useState<string | null>(null);
  const [createdPhoneNumberId, setCreatedPhoneNumberId] = useState<string | null>(null);

  // Step 3: Phone provisioning
  const [phoneOption, setPhoneOption] = useState<'new' | 'existing'>('new');
  const [country, setCountry] = useState('US');
  const [areaCode, setAreaCode] = useState('');

  // Step 4: Test call
  const [testPhone, setTestPhone] = useState('');
  const [callStatus, setCallStatus] = useState<CallStatus>('idle');
  const callPollRef = useRef<NodeJS.Timeout | null>(null);

  const totalSteps = 5;

  useEffect(() => {
    const ref = callPollRef;
    return () => {
      if (ref.current) clearInterval(ref.current);
    };
  }, []);

  const handleSkip = async () => {
    analytics.track('onboarding_skipped', { step });
    try {
      await skipOnboarding();
      onClose();
    } catch {
      onClose();
    }
  };

  const goNext = () => setStep((s) => Math.min(s + 1, totalSteps));
  const goPrev = () => {
    setError(null);
    setStep((s) => Math.max(s - 1, 1));
  };

  // ── Step 2: Create Agent ──────────────────────────────────────────────────
  const handleCreateAgent = async () => {
    setError(null);
    setLoading(true);
    try {
      const personalityPrompts: Record<string, string> = {
        friendly: 'Be warm, empathetic, and conversational. Use casual language and show genuine interest.',
        professional: 'Be formal, precise, and efficient. Maintain a business-like tone throughout.',
        casual: 'Be relaxed, natural, and personable. Speak like a helpful friend.',
      };

      const res = await api.post('/api/v1/agents', {
        agent_name: agentName,
        description: `AI agent created during onboarding`,
        primary_language: 'en-US',
        supported_languages: ['en-US'],
        voice_id: voiceId,
        personality_prompt: personalityPrompts[personality],
        greeting_message: greeting,
        fallback_message: "I'm sorry, I didn't quite catch that. Could you repeat?",
        max_conversation_turns: 10,
        session_timeout_minutes: 15,
      });

      const agentId = res.data?.agent?.agent_id || res.data?.agent_id || res.data?.id;
      setCreatedAgentId(agentId);
      await completeStep('create_agent');
      analytics.track('onboarding_step_completed', { step: 2, stepName: 'create_agent' });
      goNext();
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Failed to create agent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: Add Phone ─────────────────────────────────────────────────────
  const handleGetNumber = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await api.post('/api/v1/phones/provision', {
        country_code: country,
        area_code: areaCode || undefined,
        agent_id: createdAgentId,
      });
      const phoneNumberId = res.data?.phone?.phone_number_id || res.data?.phone_number_id;
      setCreatedPhoneNumberId(phoneNumberId);
      await completeStep('add_phone');
      analytics.track('onboarding_step_completed', { step: 3, stepName: 'add_phone' });
      goNext();
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Failed to provision number. Try again or skip.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipPhone = async () => {
    await completeStep('add_phone').catch(() => {});
    analytics.track('onboarding_skipped', { step: 3 });
    goNext();
  };

  // ── Step 4: Test Call ─────────────────────────────────────────────────────
  const handleTestCall = async () => {
    if (!testPhone.trim()) {
      setError('Please enter your phone number');
      return;
    }
    setError(null);
    setCallStatus('calling');

    try {
      // Find first available phone number for this agent, or use any provisioned number
      let phoneNumberId = createdPhoneNumberId;

      if (!phoneNumberId) {
        // Fallback: get first active phone number
        const phonesRes = await api.get('/api/v1/phones');
        const phones = phonesRes.data?.phones || phonesRes.data?.data || [];
        if (phones.length > 0) {
          phoneNumberId = phones[0].phone_number_id;
        }
      }

      if (!phoneNumberId || !createdAgentId) {
        setError('No phone number or agent available for test call. Complete previous steps first.');
        setCallStatus('error');
        return;
      }

      await api.post('/api/v1/calls/outbound', {
        to: testPhone.startsWith('+') ? testPhone : `+1${testPhone.replace(/\D/g, '')}`,
        agent_id: createdAgentId,
        phone_number_id: phoneNumberId,
      });

      setCallStatus('connected');

      // Simulate call completing after 5 seconds
      setTimeout(() => {
        setCallStatus('complete');
        analytics.track('onboarding_step_completed', { step: 4, stepName: 'test_call' });
        completeStep('test_call').catch(() => {});
      }, 5000);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Failed to initiate test call');
      setCallStatus('error');
    }
  };

  // ── Step 5: Launch ────────────────────────────────────────────────────────
  const handleFinish = async () => {
    // Fire confetti
    confetti({
      particleCount: 150,
      spread: 80,
      origin: { y: 0.6 },
      colors: ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'],
    });
    await completeStep('create_campaign').catch(() => {});
    analytics.track('onboarding_completed', {});
    setTimeout(onComplete, 800);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="relative w-full max-w-xl mx-4 rounded-2xl shadow-2xl overflow-hidden"
        style={{ background: '#0f1117', border: '1px solid rgba(139,92,246,0.3)' }}
      >
        {/* Progress bar */}
        <div className="h-1 w-full" style={{ background: '#1e2030' }}>
          <div
            className="h-full transition-all duration-500"
            style={{
              width: `${((step - 1) / (totalSteps - 1)) * 100}%`,
              background: 'linear-gradient(90deg, #8b5cf6, #06b6d4)',
            }}
          />
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-between px-6 pt-4 pb-2">
          <span className="text-xs" style={{ color: '#6b7280' }}>
            Step {step} of {totalSteps}
          </span>
          {step > 1 && (
            <button
              onClick={goPrev}
              className="text-xs px-3 py-1 rounded-lg transition-colors"
              style={{ color: '#9ca3af', background: 'rgba(255,255,255,0.05)' }}
            >
              ← Back
            </button>
          )}
        </div>

        {/* Content area */}
        <div className="px-8 pb-8 pt-2">
          {step === 1 && <StepWelcome onNext={goNext} onSkip={handleSkip} />}
          {step === 2 && (
            <StepCreateAgent
              agentName={agentName}
              setAgentName={setAgentName}
              voiceId={voiceId}
              setVoiceId={setVoiceId}
              personality={personality}
              setPersonality={setPersonality}
              greeting={greeting}
              setGreeting={setGreeting}
              onSubmit={handleCreateAgent}
              loading={loading}
              error={error}
            />
          )}
          {step === 3 && (
            <StepAddPhone
              phoneOption={phoneOption}
              setPhoneOption={setPhoneOption}
              country={country}
              setCountry={setCountry}
              areaCode={areaCode}
              setAreaCode={setAreaCode}
              onSubmit={handleGetNumber}
              onSkip={handleSkipPhone}
              loading={loading}
              error={error}
            />
          )}
          {step === 4 && (
            <StepTestCall
              testPhone={testPhone}
              setTestPhone={setTestPhone}
              callStatus={callStatus}
              onCall={handleTestCall}
              onNext={goNext}
              error={error}
            />
          )}
          {step === 5 && (
            <StepLaunch
              onFinish={handleFinish}
              onClose={onClose}
              navigate={navigate}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const StepWelcome: React.FC<{ onNext: () => void; onSkip: () => void }> = ({ onNext, onSkip }) => (
  <div className="text-center">
    <div className="text-5xl mb-4">👋</div>
    <h2 className="text-2xl font-bold mb-2" style={{ color: '#f1f5f9' }}>
      Welcome to CallPilot
    </h2>
    <p className="mb-6 text-sm" style={{ color: '#9ca3af' }}>
      Let's get you set up in under 5 minutes.
    </p>
    <ul className="text-left space-y-3 mb-8">
      {[
        '🤖  Create AI voice agents that handle calls automatically',
        '📞  Launch outbound campaigns to thousands of contacts',
        '📊  Track outcomes with real-time analytics & call recordings',
      ].map((item) => (
        <li
          key={item}
          className="flex items-start gap-3 text-sm px-4 py-3 rounded-xl"
          style={{ background: 'rgba(139,92,246,0.08)', color: '#d1d5db' }}
        >
          {item}
        </li>
      ))}
    </ul>
    <button
      onClick={onNext}
      className="w-full py-3 rounded-xl font-semibold text-white transition-opacity hover:opacity-90"
      style={{ background: 'linear-gradient(135deg, #8b5cf6, #06b6d4)' }}
    >
      Let's get you set up →
    </button>
    <button
      onClick={onSkip}
      className="mt-3 text-xs underline block mx-auto"
      style={{ color: '#6b7280' }}
    >
      Skip setup
    </button>
  </div>
);

const PERSONALITY_OPTIONS: Array<{ value: 'friendly' | 'professional' | 'casual'; label: string }> = [
  { value: 'friendly', label: '😊 Friendly' },
  { value: 'professional', label: '💼 Professional' },
  { value: 'casual', label: '😎 Casual' },
];

const StepCreateAgent: React.FC<{
  agentName: string; setAgentName: (v: string) => void;
  voiceId: string; setVoiceId: (v: string) => void;
  personality: 'friendly' | 'professional' | 'casual'; setPersonality: (v: any) => void;
  greeting: string; setGreeting: (v: string) => void;
  onSubmit: () => void; loading: boolean; error: string | null;
}> = ({ agentName, setAgentName, voiceId, setVoiceId, personality, setPersonality, greeting, setGreeting, onSubmit, loading, error }) => (
  <div>
    <h2 className="text-xl font-bold mb-1" style={{ color: '#f1f5f9' }}>Create an AI agent</h2>
    <p className="text-sm mb-6" style={{ color: '#9ca3af' }}>Your agent will handle calls for you.</p>

    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium mb-1" style={{ color: '#9ca3af' }}>Agent name</label>
        <input
          value={agentName}
          onChange={(e) => setAgentName(e.target.value)}
          placeholder="e.g. Sales Assistant"
          className="w-full px-3 py-2 rounded-lg text-sm outline-none focus:ring-1 focus:ring-purple-500"
          style={{ background: '#1e2030', border: '1px solid rgba(255,255,255,0.1)', color: '#f1f5f9' }}
        />
      </div>

      <div>
        <label className="block text-xs font-medium mb-1" style={{ color: '#9ca3af' }}>Voice</label>
        <select
          value={voiceId}
          onChange={(e) => setVoiceId(e.target.value)}
          className="w-full px-3 py-2 rounded-lg text-sm outline-none focus:ring-1 focus:ring-purple-500"
          style={{ background: '#1e2030', border: '1px solid rgba(255,255,255,0.1)', color: '#f1f5f9' }}
        >
          {VOICES.map((v) => (
            <option key={v.id} value={v.id}>{v.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium mb-2" style={{ color: '#9ca3af' }}>Personality</label>
        <div className="flex gap-2">
          {PERSONALITY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPersonality(opt.value)}
              className="flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all"
              style={{
                background: personality === opt.value ? 'rgba(139,92,246,0.3)' : 'rgba(255,255,255,0.05)',
                border: personality === opt.value ? '1px solid #8b5cf6' : '1px solid rgba(255,255,255,0.1)',
                color: personality === opt.value ? '#c4b5fd' : '#9ca3af',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium mb-1" style={{ color: '#9ca3af' }}>Opening greeting</label>
        <textarea
          value={greeting}
          onChange={(e) => setGreeting(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 rounded-lg text-sm outline-none focus:ring-1 focus:ring-purple-500 resize-none"
          style={{ background: '#1e2030', border: '1px solid rgba(255,255,255,0.1)', color: '#f1f5f9' }}
        />
      </div>
    </div>

    {error && (
      <p className="mt-3 text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(239,68,68,0.1)', color: '#f87171' }}>
        {error}
      </p>
    )}

    <button
      onClick={onSubmit}
      disabled={loading || !agentName.trim()}
      className="mt-6 w-full py-3 rounded-xl font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
      style={{ background: 'linear-gradient(135deg, #8b5cf6, #06b6d4)' }}
    >
      {loading ? 'Creating...' : 'Create Agent →'}
    </button>
  </div>
);

const COUNTRIES = [
  { code: 'US', label: '🇺🇸 United States (+1)' },
  { code: 'GB', label: '🇬🇧 United Kingdom (+44)' },
  { code: 'AU', label: '🇦🇺 Australia (+61)' },
  { code: 'CA', label: '🇨🇦 Canada (+1)' },
  { code: 'JP', label: '🇯🇵 Japan (+81)' },
];

const StepAddPhone: React.FC<{
  phoneOption: 'new' | 'existing'; setPhoneOption: (v: any) => void;
  country: string; setCountry: (v: string) => void;
  areaCode: string; setAreaCode: (v: string) => void;
  onSubmit: () => void; onSkip: () => void;
  loading: boolean; error: string | null;
}> = ({ phoneOption, setPhoneOption, country, setCountry, areaCode, setAreaCode, onSubmit, onSkip, loading, error }) => (
  <div>
    <h2 className="text-xl font-bold mb-1" style={{ color: '#f1f5f9' }}>Get a phone number</h2>
    <p className="text-sm mb-6" style={{ color: '#9ca3af' }}>Your agent needs a number to make and receive calls.</p>

    <div className="flex gap-2 mb-6">
      {(['new', 'existing'] as const).map((opt) => (
        <button
          key={opt}
          onClick={() => setPhoneOption(opt)}
          className="flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all"
          style={{
            background: phoneOption === opt ? 'rgba(139,92,246,0.25)' : 'rgba(255,255,255,0.05)',
            border: phoneOption === opt ? '1px solid #8b5cf6' : '1px solid rgba(255,255,255,0.1)',
            color: phoneOption === opt ? '#c4b5fd' : '#9ca3af',
          }}
        >
          {opt === 'new' ? '✨ Provision new' : '🔗 Use existing'}
        </button>
      ))}
    </div>

    {phoneOption === 'new' && (
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: '#9ca3af' }}>Country</label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none focus:ring-1 focus:ring-purple-500"
            style={{ background: '#1e2030', border: '1px solid rgba(255,255,255,0.1)', color: '#f1f5f9' }}
          >
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: '#9ca3af' }}>Area code (optional)</label>
          <input
            value={areaCode}
            onChange={(e) => setAreaCode(e.target.value)}
            placeholder="e.g. 415"
            maxLength={5}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none focus:ring-1 focus:ring-purple-500"
            style={{ background: '#1e2030', border: '1px solid rgba(255,255,255,0.1)', color: '#f1f5f9' }}
          />
        </div>
      </div>
    )}

    {phoneOption === 'existing' && (
      <p className="text-sm px-4 py-3 rounded-xl" style={{ background: 'rgba(6,182,212,0.08)', color: '#67e8f9' }}>
        You can connect an existing number in <strong>Settings → Phone Numbers</strong> after setup.
      </p>
    )}

    {error && (
      <p className="mt-3 text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(239,68,68,0.1)', color: '#f87171' }}>
        {error}
      </p>
    )}

    <div className="mt-6 space-y-2">
      {phoneOption === 'new' && (
        <button
          onClick={onSubmit}
          disabled={loading}
          className="w-full py-3 rounded-xl font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #8b5cf6, #06b6d4)' }}
        >
          {loading ? 'Provisioning...' : 'Get Number →'}
        </button>
      )}
      <button
        onClick={onSkip}
        className="w-full py-2 rounded-xl text-sm transition-colors"
        style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        I'll do this later
      </button>
    </div>
  </div>
);

const CALL_STATUS_LABELS: Record<CallStatus, string> = {
  idle: '',
  calling: '📞 Calling...',
  connected: '✅ Connected! Your agent is speaking.',
  complete: '✓ Call complete',
  error: '❌ Call failed',
};

const StepTestCall: React.FC<{
  testPhone: string; setTestPhone: (v: string) => void;
  callStatus: CallStatus;
  onCall: () => void; onNext: () => void;
  error: string | null;
}> = ({ testPhone, setTestPhone, callStatus, onCall, onNext, error }) => (
  <div>
    <h2 className="text-xl font-bold mb-1" style={{ color: '#f1f5f9' }}>Test your agent</h2>
    <p className="text-sm mb-6" style={{ color: '#9ca3af' }}>
      Enter your number to receive a real call from your AI agent.
    </p>

    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium mb-1" style={{ color: '#9ca3af' }}>Your phone number</label>
        <input
          value={testPhone}
          onChange={(e) => setTestPhone(e.target.value)}
          placeholder="+1 415 555 0100"
          disabled={callStatus !== 'idle' && callStatus !== 'error'}
          className="w-full px-3 py-2 rounded-lg text-sm outline-none focus:ring-1 focus:ring-purple-500 disabled:opacity-60"
          style={{ background: '#1e2030', border: '1px solid rgba(255,255,255,0.1)', color: '#f1f5f9' }}
        />
      </div>

      {callStatus !== 'idle' && (
        <div
          className="px-4 py-3 rounded-xl text-sm font-medium text-center"
          style={{
            background: callStatus === 'complete'
              ? 'rgba(16,185,129,0.15)'
              : callStatus === 'error'
              ? 'rgba(239,68,68,0.1)'
              : 'rgba(139,92,246,0.15)',
            color: callStatus === 'complete' ? '#34d399' : callStatus === 'error' ? '#f87171' : '#c4b5fd',
          }}
        >
          {CALL_STATUS_LABELS[callStatus]}
        </div>
      )}

      {(error && callStatus === 'error') && (
        <p className="text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(239,68,68,0.1)', color: '#f87171' }}>
          {error}
        </p>
      )}
    </div>

    <div className="mt-6 space-y-2">
      {(callStatus === 'idle' || callStatus === 'error') && (
        <button
          onClick={onCall}
          disabled={!testPhone.trim()}
          className="w-full py-3 rounded-xl font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #8b5cf6, #06b6d4)' }}
        >
          📞 Call me now
        </button>
      )}
      {(callStatus === 'calling' || callStatus === 'connected') && (
        <div className="w-full py-3 rounded-xl text-center text-sm font-medium" style={{ background: 'rgba(139,92,246,0.15)', color: '#c4b5fd' }}>
          <span className="inline-block animate-pulse mr-2">●</span> {callStatus === 'calling' ? 'Initiating call...' : 'Agent is live'}
        </div>
      )}
      <button
        onClick={onNext}
        className="w-full py-2 rounded-xl text-sm transition-colors"
        style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        {callStatus === 'complete' ? 'Continue →' : 'Skip for now'}
      </button>
    </div>
  </div>
);

const StepLaunch: React.FC<{
  onFinish: () => void;
  onClose: () => void;
  navigate: (path: string) => void;
}> = ({ onFinish, onClose, navigate }) => (
  <div className="text-center">
    <div className="text-5xl mb-4">🚀</div>
    <h2 className="text-2xl font-bold mb-2" style={{ color: '#f1f5f9' }}>You're ready to launch!</h2>
    <p className="text-sm mb-8" style={{ color: '#9ca3af' }}>
      Your AI agent is set up. Choose how you'd like to get started.
    </p>

    <div className="grid grid-cols-2 gap-3 mb-6">
      <button
        onClick={() => { onFinish(); setTimeout(() => navigate('/campaigns'), 900); }}
        className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:scale-105"
        style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)' }}
      >
        <span className="text-2xl">📣</span>
        <span className="text-sm font-semibold" style={{ color: '#c4b5fd' }}>Start a Campaign</span>
        <span className="text-xs" style={{ color: '#6b7280' }}>Outbound calling at scale</span>
      </button>
      <button
        onClick={() => { onFinish(); setTimeout(() => navigate('/research'), 900); }}
        className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:scale-105"
        style={{ background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.25)' }}
      >
        <span className="text-2xl">🔬</span>
        <span className="text-sm font-semibold" style={{ color: '#67e8f9' }}>Market Research</span>
        <span className="text-xs" style={{ color: '#6b7280' }}>Run an automated study</span>
      </button>
    </div>

    <button
      onClick={onClose}
      className="w-full py-3 rounded-xl font-semibold text-white transition-opacity hover:opacity-90"
      style={{ background: 'linear-gradient(135deg, #8b5cf6, #06b6d4)' }}
    >
      Go to Dashboard
    </button>
  </div>
);

export default OnboardingWizard;
